import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { cx } from '@linaria/core';
import { withAuth } from 'hocs/withAuth/withAuth';
import { useAuthActions } from 'modules/Auth/AuthContext';
import { Button } from 'components/Button';
import { ButtonGroup } from 'components/deprecated/Button/ButtonGroup';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { useCurrentStore } from 'modules/Listings/store/useCurrentStore';
import { useCreateApplicationModal } from 'modules/Apps/hooks';
import { flex } from 'styles/utils';
import { AuthModalState } from 'modules/Auth/components/AuthModal';
import { useOpenJoinOrganizationModalWhenInvited } from 'modules/Organizations/components/JoinOrganization/JoinOrganizationModal';
import { SidebarMobileV2 } from 'components/SidebarV2/Sidebar';
import { useOrgInvitationNotification } from 'modules/Organizations/components/OrgInvitation';
import { CreditChips } from 'modules/User/components/Settings/Billing/CreditSummary/CreditChips';
import { NotificationButton, MenuButton, HelpButton, NavLink } from './components';
import { nav, navContent, navAuthWrapper, navContentInner } from './Navbar.styles';
import { BreadcrumbsLink } from './components/BreadcrumbsLink';
import { CreateButton } from './components/CreateButton';
import { useNotification } from './components/Notifications/useNotification';

type NavbarProps = { isLoggedIn?: boolean; className?: string };

export const NavbarInternal: React.FC<NavbarProps> = ({ isLoggedIn, className }) => {
  const { logout, openLoginModal } = useAuthActions();
  const router = useRouter();
  const { track } = useDataCollection();
  const { load } = useCurrentStore();
  const currentPath = router.asPath;
  const [showProfile, setShowProfile] = useState(false);

  useOpenJoinOrganizationModalWhenInvited();
  useOrgInvitationNotification();
  useNotification();

  const { canCreateApplication, openCreateApplicationModal } = useCreateApplicationModal({
    onAppCreated: (createdApp, fromTemplate = false) => {
      load();
      router.push(`/${createdApp.userId}/${createdApp.appId}${!fromTemplate ? '?show_wizard_model_path_modal=true' : ''}`);
      track('User App Created', { context: 'regular' });
    },
  });

  // to ensure react doesnt show hydration mismatch on client vs server render
  useEffect(() => {
    setShowProfile(Boolean(isLoggedIn));
  }, [isLoggedIn]);

  const handleLogout = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault();
      logout();
    },
    [logout],
  );

  const onHeaderBtnClick = (btnText: string): void => {
    track('Navbar Button Clicked', { text: btnText, context: 'regular' });
  };

  const handleCreateApp = (): void => {
    onHeaderBtnClick('create_app');
    openCreateApplicationModal();
  };

  return (
    <nav className={cx(nav, className)} data-testid="navbar">
      <div className={navContent}>
        <div className={navContentInner}>
          <div className={cx(flex.init, flex.alignItems.center, flex.gap[1], flex.flex.one)}>
            <SidebarMobileV2 />
            <BreadcrumbsLink />
          </div>

          {showProfile ? (
            <ButtonGroup
              key="logged-in"
              className={navAuthWrapper}
              style={{
                minWidth: 0,
              }}
            >
              <CreditChips />
              <NotificationButton key="notification-button" />
              <CreateButton />
              <MenuButton onLogout={handleLogout} canCreateApp={canCreateApplication} handleCreateApp={handleCreateApp} />
            </ButtonGroup>
          ) : (
            <ButtonGroup key="logged-out" className={navAuthWrapper}>
              <HelpButton />

              <NavLink href="/login" currentPath={currentPath}>
                <Button
                  variant="tertiary"
                  size="md"
                  onClick={(e) => {
                    e.preventDefault();
                    onHeaderBtnClick('Login');
                    openLoginModal();
                  }}
                >
                  Log in
                </Button>
              </NavLink>

              {process.env.NEXT_PUBLIC_SIGN_UP_DISABLED !== 'true' && (
                <NavLink href="/signup" currentPath={currentPath}>
                  <Button
                    size="md"
                    onClick={(e) => {
                      e.preventDefault();
                      onHeaderBtnClick('Signup');
                      openLoginModal(AuthModalState.SHOW_SIGN_UP);
                    }}
                  >
                    Sign up
                  </Button>
                </NavLink>
              )}
            </ButtonGroup>
          )}
        </div>
      </div>
    </nav>
  );
};

export const Navbar = withAuth<NavbarProps>()(NavbarInternal);
